import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Sparkles, Lock, User, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import Logo from '../components/Logo';
import api, { setToken } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import PasswordStrengthMeter from '../components/PasswordStrengthMeter';
import { scorePassword } from '../utils/passwordStrength';

export default function AcceptInvite() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { setUser } = useAuth();
  const token = params.get('token');
  const method = params.get('method');
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [googleConfig, setGoogleConfig] = useState(null);
  const [googleWorking, setGoogleWorking] = useState(false);

  // Load Google OAuth config in parallel with the invite preview
  useEffect(() => {
    api.get('/integrations/google/config').then(({ data }) => setGoogleConfig(data)).catch(() => setGoogleConfig({ configured: false }));
  }, []);

  const startGoogle = () => {
    if (!googleConfig?.configured || !googleConfig?.client_id || !token) return;
    setGoogleWorking(true);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
    const redirectUri = `${window.location.origin}/auth/google`;
    const state = `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    sessionStorage.setItem('google_oauth_state', state);
    sessionStorage.setItem('google_oauth_invite_token', token);
    sessionStorage.setItem('google_oauth_after', '/app');
    const scopes = (googleConfig.scopes_sso && googleConfig.scopes_sso.length)
      ? googleConfig.scopes_sso
      : ['openid', 'email', 'profile'];
    const qs = new URLSearchParams({
      client_id: googleConfig.client_id,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: scopes.join(' '),
      prompt: 'select_account',
      state,
      login_hint: invite?.email || '',
    });
    const url = `https://accounts.google.com/o/oauth2/v2/auth?${qs.toString()}`;
    if (window.top && window.top !== window.self) {
      try { window.top.location.href = url; return; } catch { /* cross-origin */ }
    }
    window.location.href = url;
  };

  // Auto-trigger Google flow when ?method=google is in the URL (clicked from
  // the "Continuer avec Google" button in the invitation email).
  useEffect(() => {
    if (method === 'google' && invite && googleConfig?.configured && !googleWorking) {
      startGoogle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [method, invite, googleConfig]);

  useEffect(() => {
    if (!token) { setError('Lien invalide'); setLoading(false); return; }
    (async () => {
      try {
        const { data } = await api.get(`/auth/invite/preview?token=${encodeURIComponent(token)}`);
        setInvite(data);
      } catch (err) {
        const status = err?.response?.status;
        const serverMsg = err?.response?.data?.detail;
        let msg = serverMsg || 'Invitation introuvable';
        if (status === 404) {
          msg = "Cette invitation n'existe pas (ou plus) sur ce serveur. Vérifiez que vous avez ouvert le lien depuis le bon environnement (preview vs production) — le token est lié à la base de l'émetteur.";
        } else if (status === 410 || /expired/i.test(String(serverMsg))) {
          msg = 'Cette invitation a expiré. Demandez à votre administrateur de la renvoyer.';
        } else if (status === 409 || /consum/i.test(String(serverMsg))) {
          msg = 'Cette invitation a déjà été utilisée. Connectez-vous avec votre mot de passe, ou demandez une réinitialisation.';
        } else if (status === 403) {
          msg = "Accès refusé par le serveur. Cela peut venir d'une règle de sécurité (CDN/WAF). Contactez votre administrateur.";
        }
        setError(msg);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password) {
      if (password.length < 8) { setError('Le mot de passe doit contenir au moins 8 caractères.'); return; }
      const { common, score } = scorePassword(password);
      if (common) { setError('Ce mot de passe est trop courant — choisissez-en un autre.'); return; }
      if (score < 2) { setError('Mot de passe trop faible.'); return; }
    }
    setSubmitting(true);
    try {
      const { data } = await api.post('/auth/invite/accept', {
        token,
        password: password || undefined,
        name: name || undefined,
      });
      if (data.session_token) setToken(data.session_token);
      setUser(data.user);
      navigate('/app', { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Acceptation impossible');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-6 h-6 animate-spin text-[#4F46E5]" />
      </div>
    );
  }

  if (error && !invite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="max-w-[380px] text-center px-6">
          <div className="w-14 h-14 rounded-2xl bg-[#FEF2F2] flex items-center justify-center mx-auto">
            <AlertCircle className="w-7 h-7 text-[#EF4444]" />
          </div>
          <h1 className="mt-4 text-[20px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Invitation indisponible</h1>
          <p className="mt-2 text-[13.5px] text-[#6B6C85]">{error}</p>
          <button onClick={() => navigate('/login', { replace: true })} className="mt-5 h-10 px-5 rounded-xl brand-gradient text-white text-[13px] font-semibold">
            Retour à la connexion
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-6">
      <div className="w-full max-w-[440px]">
        <div className="mb-10" data-testid="invite-brand">
          <Logo size={48} variant="full" />
        </div>

        <div className="p-4 mb-6 rounded-xl bg-[#EEF2FF] border border-[#C7D2FE] flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl brand-gradient flex items-center justify-center shrink-0">
            <CheckCircle2 className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <div className="text-[13px] text-[#4A4B63]">
              <strong>{invite.invited_by}</strong> vous invite à rejoindre
            </div>
            <div className="text-[15px] font-bold text-[#1A1B2E] mt-0.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {invite.org_name}
            </div>
            <div className="mt-1 flex items-center gap-2 text-[11.5px] text-[#6B6C85]">
              <span>{invite.email}</span>
              <span>·</span>
              <span className="font-semibold px-1.5 py-0.5 rounded bg-white text-[#4F46E5]">{invite.role}</span>
            </div>
          </div>
        </div>

        <h1 className="text-[24px] font-bold text-[#1A1B2E] leading-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Activer votre compte
        </h1>
        <p className="text-[13.5px] text-[#6B6C85] mt-2 mb-5">
          Activez votre compte en un clic via Google, ou définissez un mot de passe.
        </p>

        {googleConfig?.configured && (
          <>
            <button
              type="button"
              data-testid="invite-google-btn"
              onClick={startGoogle}
              disabled={googleWorking}
              className="w-full h-11 rounded-xl bg-white border border-[#E5E7EB] text-[#1A1B2E] font-semibold text-[14px] flex items-center justify-center gap-3 hover:bg-[#FAFAFE] transition-colors disabled:opacity-60 mb-4"
            >
              {googleWorking ? (
                <Loader2 className="w-4 h-4 animate-spin text-[var(--ofm-primary)]" />
              ) : (
                <svg className="w-5 h-5" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C33.7 6 29 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.7-.4-3.9z" />
                  <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.8 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C33.7 6 29 4 24 4 16.3 4 9.7 8.4 6.3 14.7z" />
                  <path fill="#4CAF50" d="M24 44c5 0 9.5-1.9 12.9-5.1l-6-4.9c-2 1.4-4.4 2.2-6.9 2.2-5.3 0-9.8-3.4-11.3-8.1l-6.5 5C9.5 39.5 16.2 44 24 44z" />
                  <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.1 4-3.9 5.3l6 4.9c4.2-3.9 6.6-9.6 6.6-16.3 0-1.3-.1-2.6-.4-3.8z" />
                </svg>
              )}
              Continuer avec Google
            </button>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-[#EFF0F6]" />
              <span className="text-[11px] text-[#8E8FA5] uppercase tracking-wider">ou</span>
              <div className="flex-1 h-px bg-[#EFF0F6]" />
            </div>
          </>
        )}

        <form onSubmit={submit} className="space-y-3">
          <div className="relative">
            <User className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8E8FA5]" />
            <input
              data-testid="invite-name"
              type="text"
              placeholder="Votre nom complet (optionnel)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full h-11 pl-10 pr-4 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[14px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none transition-colors"
            />
          </div>
          <div className="relative">
            <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8E8FA5]" />
            <input
              data-testid="invite-password"
              type="password"
              placeholder="Mot de passe (optionnel)"
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              className="w-full h-11 pl-10 pr-4 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[14px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none transition-colors"
            />
          </div>
          <PasswordStrengthMeter value={password} />
          {error && (
            <div data-testid="invite-error" className="text-[12px] text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-3 py-2">{error}</div>
          )}
          <button
            data-testid="invite-submit"
            type="submit"
            disabled={submitting}
            className="w-full h-11 rounded-xl brand-gradient text-white font-semibold text-[14px] flex items-center justify-center gap-2 shadow-sm hover:opacity-95 transition-opacity disabled:opacity-60"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Rejoindre {invite.org_name}
          </button>
        </form>
      </div>
    </div>
  );
}
